import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { api, RSD } from "../../lib/api";
import { Sparkles } from "lucide-react";
import { OverviewTab, PaymentTab, TimingTab, LatenessTab, PickupTab, DiscountTab }
  from "./analytics/OrderAnalytics";
import { RestaurantFunnelTab, TimeTab, ProductsTab, VistaTab } from "./analytics/RestaurantAnalytics";
import PromotionsTab from "./analytics/PromotionsTab";

const RANGES = [["today", "Danas"], ["yesterday", "Juče"], ["last_7", "7 dana"], ["last_28", "28 dana"],
                ["this_month", "Ovaj mesec"], ["previous_month", "Prošli mesec"]];
const PRESET_DAYS = { today: 1, yesterday: 2, last_7: 7, last_28: 28, this_month: 30, previous_month: 60 };
const SOURCES = [["all", "Sve"], ["ponesi", "Ponesi.rs"], ["vista", "Vista"]];
// NOTE: KPI cards now come from the server-authoritative order analytics (see analytics/OrderAnalytics).
const TABS = [["pregled", "Pregled"], ["funnel", "Funnel"], ["vreme", "Vreme"], ["proizvodi", "Proizvodi"],
              ["placanje", "Plaćanje"], ["vremena", "Operacije"], ["kasnjenje", "Kašnjenje"],
              ["preuzimanje", "Preuzimanje"], ["popusti", "Popusti"], ["promocije", "Promocije"], ["vista", "Vista"]];

function Stat({ label, value, testid }) {   // eslint-disable-line no-unused-vars
  return (
    <div className="bg-white rounded-2xl border p-3">
      <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">{label}</p>
      <p data-testid={testid} className="font-display font-black text-xl mt-0.5">{value}</p>
    </div>
  );
}

function HourChart({ byHour }) {
  const max = Math.max(1, ...byHour.map((h) => h.orders));
  const shown = byHour.filter((h) => h.hour >= 7);
  return (
    <div data-testid="analytics-hours" className="bg-white rounded-2xl border p-3">
      <div className="flex items-end gap-1 h-32">
        {shown.map((h) => (
          <div key={h.hour} className="flex-1 flex flex-col items-center justify-end h-full" title={`${h.hour}:00 · ${h.orders} porudžbina · ${RSD(h.revenue)}`}>
            <div data-testid={`hour-bar-${h.hour}`} style={{ height: `${(h.orders / max) * 100}%` }}
              className={`w-full rounded-t ${h.orders ? "bg-brand-red" : "bg-slate-100"} min-h-[2px]`} />
          </div>
        ))}
      </div>
      <div className="flex gap-1 mt-1">
        {shown.map((h) => <span key={h.hour} className="flex-1 text-center text-[8px] text-slate-400">{h.hour}</span>)}
      </div>
    </div>
  );
}

export default function AnalyticsPage() {
  const { restaurant } = useOutletContext();
  const rid = restaurant.id;
  const navigate = useNavigate();
  const [days, setDays] = useState("last_7");
  const [source, setSource] = useState("all");
  const [data, setData] = useState(null);
  const [recs, setRecs] = useState(null);
  const [tab, setTab] = useState("pregled");
  const [oa, setOa] = useState(null);
  const [ncm, setNcm] = useState(null);

  useEffect(() => {
    setOa(null);
    api.get(`/restaurant/${rid}/analytics/full?preset=${days}&source=${source}`)
      .then(({ data }) => setOa(data)).catch(() => setOa(null));
  }, [rid, days, source]);

  useEffect(() => {
    api.get(`/restaurant/${rid}/analytics?days=${PRESET_DAYS[days] || 7}&source=${source}`).then(({ data }) => setData(data)).catch(() => setData(null));
  }, [rid, days, source]);
  useEffect(() => {
    setNcm(null);
    api.get(`/restaurant/${rid}/analytics/new-customers?preset=${days}`)
      .then(({ data }) => setNcm(data)).catch(() => setNcm(null));
  }, [rid, days]);
  useEffect(() => {
    api.get(`/restaurant/${rid}/analytics/recommendations`).then(({ data }) => setRecs(data)).catch(() => setRecs(null));
  }, [rid]);

  const top = useMemo(() => (data?.products || []).slice(0, 5), [data]);
  const bottom = useMemo(() => (data?.products || []).slice(-5).reverse(), [data]);

  const createFromRec = (r) => {
    sessionStorage.setItem("ponesi_hh_prefill", JSON.stringify({
      product_id: r.product_id, start_time: r.start_time, end_time: r.end_time,
      discount_type: r.discount_type, discount_value: r.discount_value,
      ...(r.days ? { days: r.days } : {}),
    }));
    navigate(`/restaurant/${rid}/happy-hour`);
  };

  return (
    <div className="max-w-3xl" data-testid="analytics-page">
      <h1 className="font-display font-black text-2xl mb-4">Analitika</h1>

      <div className="flex flex-wrap gap-2 mb-2">
        {RANGES.map(([d, l]) => (
          <button key={d} data-testid={`an-range-${d}`} onClick={() => setDays(d)}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold ${days === d ? "bg-brand-red hover:bg-brand-redHover text-white" : "bg-slate-100"}`}>{l}</button>
        ))}
      </div>
      <div className="flex gap-2 mb-4">
        {SOURCES.map(([v, l]) => (
          <button key={v} data-testid={`an-src-${v}`} onClick={() => setSource(v)}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold ${source === v ? "bg-slate-800 text-white" : "bg-slate-100"}`}>{l}</button>
        ))}
      </div>

      <div className="flex flex-wrap gap-1 mb-4 border-b pb-2">
        {TABS.map(([k, l]) => (
          <button key={k} data-testid={`oa-tab-${k}`} onClick={() => setTab(k)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold ${tab === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>
        ))}
      </div>

      {tab === "promocije" && <PromotionsTab rid={rid} />}

      {tab !== "pregled" && tab !== "promocije" && (
        !oa ? <p className="text-slate-400 text-sm" data-testid="oa-loading">Učitavanje…</p> : (
          <>
            {tab === "placanje" && <PaymentTab d={oa} />}
            {tab === "vremena" && <TimingTab d={oa} />}
            {tab === "kasnjenje" && <LatenessTab d={oa} />}
            {tab === "preuzimanje" && <PickupTab d={oa} />}
            {tab === "popusti" && <DiscountTab d={oa} />}
            {tab === "funnel" && <RestaurantFunnelTab d={oa} />}
            {tab === "vreme" && <TimeTab d={oa} />}
            {tab === "proizvodi" && <ProductsTab d={oa} />}
            {tab === "vista" && <VistaTab d={oa} />}
          </>
        )
      )}

      {tab === "pregled" && oa && <div className="mb-5"><OverviewTab d={oa} /></div>}

      {tab === "pregled" && ncm && (
        <div className="mb-5" data-testid="new-customers-card">
          <h2 className="font-display font-black text-base mb-2">Novi kupci</h2>
          <p className="text-[11px] text-slate-400 mb-2">Kupac se računa kao NOVI ako je njegova prva
            preuzeta Ponesi porudžbina u ovom restoranu bila u izabranom periodu.</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat label="Novi kupci" value={ncm.new_customers} testid="nc-new-customers" />
            <Stat label="Kupci koji se vraćaju" value={ncm.returning_customers} testid="nc-returning-customers" />
            <Stat label="Porudžbine novih" value={ncm.new_customer_orders} testid="nc-new-orders" />
            <Stat label="Prihod novih" value={RSD(ncm.new_customer_revenue)} testid="nc-new-revenue" />
            <Stat label="AOV novih" value={RSD(ncm.new_customer_aov)} testid="nc-new-aov" />
            <Stat label="AOV vraćenih" value={RSD(ncm.returning_customer_aov)} testid="nc-ret-aov" />
            <Stat label="Nagrađeni novi" value={ncm.rewarded_new_customers} testid="nc-rewarded" />
            <Stat label="Stopa nagrađivanja" value={`${ncm.reward_rate}%`} testid="nc-reward-rate" />
          </div>
        </div>
      )}

      {tab === "pregled" && (!data ? <p className="text-slate-400 text-sm">Učitavanje…</p> : (
        <div className="space-y-5">
          <div>
            <p className="text-xs font-black uppercase tracking-wider text-slate-400 mb-1">Prodaja po satima</p>
            <HourChart byHour={data.by_hour} />
          </div>

          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="bg-white rounded-2xl border p-3" data-testid="an-source-ponesi">
              <p className="text-[10px] font-black uppercase text-slate-400">Ponesi.rs</p>
              <p className="font-bold">{data.by_source.PONESI.orders} · {RSD(data.by_source.PONESI.revenue)}</p>
            </div>
            <div className="bg-white rounded-2xl border p-3" data-testid="an-source-vista">
              <p className="text-[10px] font-black uppercase text-slate-400">Vista</p>
              <p className="font-bold">{data.by_source.VISTA.orders} · {RSD(data.by_source.VISTA.revenue)}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div data-testid="an-top-products">
              <p className="text-xs font-black uppercase tracking-wider text-slate-400 mb-1">Najprodavaniji proizvodi</p>
              <div className="bg-white rounded-2xl border divide-y">
                {top.length === 0 && <p className="p-3 text-sm text-slate-400">Nema podataka.</p>}
                {top.map((p) => (
                  <div key={p.product_id} className="p-3 flex items-center justify-between text-sm">
                    <span className="font-bold truncate">{p.name}</span>
                    <span className="text-slate-500 shrink-0">{p.units} kom · {RSD(p.revenue)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div data-testid="an-bottom-products">
              <p className="text-xs font-black uppercase tracking-wider text-slate-400 mb-1">Najmanje prodavani</p>
              <div className="bg-white rounded-2xl border divide-y">
                {bottom.length === 0 && <p className="p-3 text-sm text-slate-400">Nema podataka.</p>}
                {bottom.map((p) => (
                  <div key={p.product_id} className="p-3 flex items-center justify-between text-sm">
                    <span className="font-bold truncate">{p.name}</span>
                    <span className="text-slate-500 shrink-0">{p.units} kom · {RSD(p.revenue)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div data-testid="an-recommendations">
            <p className="text-xs font-black uppercase tracking-wider text-slate-400 mb-1">Ponesi preporuke</p>
            {!recs || !recs.enough_data ? (
              <p data-testid="an-rec-insufficient" className="bg-white rounded-2xl border p-3 text-sm text-slate-400">
                {recs?.message || "Nema dovoljno podataka za pouzdanu preporuku."}
                {recs?.data_quality?.limitation && (
                  <span className="block text-[11px] text-slate-300 mt-1">{recs.data_quality.limitation}</span>
                )}
              </p>
            ) : recs.items.length === 0 ? (
              <p className="bg-white rounded-2xl border p-3 text-sm text-slate-500">{recs.message || "Trenutno nema perioda sa značajno nižom prodajom."}</p>
            ) : recs.items.map((r) => (
              <div key={r.product_id} data-testid="an-rec-item" className="bg-white rounded-2xl border-2 border-brand-orange/40 p-3">
                <p className="text-sm font-semibold text-slate-700">{r.reason}</p>
                <p className="text-sm font-bold mt-2">
                  Predlog: {r.product_name} · {r.day_label ? `${r.day_label} · ` : ""}{r.start_time}–{r.end_time} · -{r.discount_value}%
                </p>
                <button data-testid="an-rec-create" onClick={() => createFromRec(r)}
                  className="mt-3 rounded-xl bg-brand-red hover:bg-brand-redHover text-white px-4 py-3 font-display font-black text-sm flex items-center gap-2">
                  <Sparkles size={15} />KREIRAJ HAPPY HOUR
                </button>
              </div>
            ))}
          </div>

          {recs?.performance?.some((p) => p.during.orders > 0) && (
            <div data-testid="an-hh-performance">
              <p className="text-xs font-black uppercase tracking-wider text-slate-400 mb-1">Rezultati Happy Hour-a</p>
              <div className="bg-white rounded-2xl border divide-y">
                {recs.performance.filter((p) => p.during.orders > 0).map((p) => (
                  <div key={p.happy_hour_id} className="p-3 text-sm">
                    <p className="font-bold">{p.during.units} kom · {RSD(p.during.revenue)} u periodu akcije</p>
                    <p className="text-xs text-slate-500">
                      {p.comparable ? `Pre akcije: ${p.before.units} kom · promena ${p.units_change_pct > 0 ? "+" : ""}${p.units_change_pct}%`
                        : "Nema dovoljno uporedivih podataka iz prethodnog perioda."}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
